import { z } from 'zod';

/**
 * Mirrors apps/api/src/modules/catalog/http/category.dto.ts's
 * `categoryInputSchema` field-for-field — same names, same bounds, same
 * slug pattern as `categories_slug_format` (migration 0018). A validation
 * error surfaces before the round trip, never drifts from what the server
 * actually enforces.
 */

const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(63)
  .regex(
    SLUG_PATTERN,
    'Le slug doit être en minuscules, chiffres et tirets simples, 2 à 63 caractères.',
  );

/**
 * A native `<select>` with no `required` reports `''` for "nothing
 * chosen" (Select.tsx's own placeholder option) — normalised to `null`
 * here so an unset parent never surfaces as a spurious "invalid uuid"
 * error, the identical pattern `ProductForm`'s own `categoryId` field
 * already establishes (`features/products/schemas.ts`).
 */
const optionalParentCategoryId = z
  .union([z.literal(''), z.string().trim().uuid()])
  .nullable()
  .optional()
  .transform((value) => (value === '' || value === undefined ? null : value));

const optionalUrl = z
  .union([z.literal(''), z.string().trim().url().max(2048)])
  .nullable()
  .optional()
  .transform((value) => (value === '' || value === undefined ? null : value));

const optionalText = z
  .string()
  .trim()
  .max(5000)
  .nullable()
  .optional()
  .transform((value) => (value === '' || value === undefined ? null : value));

export const categoryFormSchema = z.object({
  name: z.string().trim().min(1, 'Le nom est requis.').max(200),
  slug: slugSchema,
  description: optionalText,
  imageUrl: optionalUrl,
  parentCategoryId: optionalParentCategoryId,
  isActive: z.boolean(),
});

export type CategoryFormValues = z.infer<typeof categoryFormSchema>;
